import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useMutation } from "@tanstack/react-query";
import ApiErrorBanner from "components/ApiErrorBanner";
import Button from "components/ui/Button";
import Textarea from "components/formElements/Textarea/Textarea";
import GoogleTranslateAttributionBadge from "components/GoogleTranslateAttributionBadge/GoogleTranslateAttributionBadge";
import LanguageSelect from "components/LanguageSelect/LanguageSelect";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { useUser } from "contexts/UserContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Language } from "services/config";
import { MAX_TRANSLATION_LENGTH, fetchTranslation } from "services/translate";
import { isRtlLang } from "utils/rtl";
import "./TranslationTool.css";
import { dynamicKey } from "services/i18n";

const DEFAULT_SRC_LANG = "en";

type SelectedLanguage = Language | undefined;

type TranslationToolProps = {
  searchText: string;
};

export default function TranslationTool(props: TranslationToolProps) {
  const { t } = useTranslation();
  const nativeLanguage = useUser().profile.native_language;

  const [srcText, setSrcText] = useState("");
  const [srcLang, setSrcLang] = useState<string | null>(DEFAULT_SRC_LANG);
  const [targetLang, setTargetLang] = useState<string | null>(nativeLanguage);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { reset, ...translation } = useMutation({ mutationFn: fetchTranslation });
  const translatedText = translation.data?.translated_text ?? "";

  // A changed search term becomes the source text and voids the old
  // translation; an unchanged term keeps the user's in-tab edits.
  useEffect(() => {
    if (props.searchText) {
      setSrcText(props.searchText);
      reset();
    }
  }, [props.searchText, reset]);

  const translate = (text: string, source: string | null, target: string | null) => {
    const errors: string[] = [];
    if (!text) errors.push("Dictionary.translate.textRequired");
    if (!target) errors.push("Dictionary.translate.languageRequired");
    setValidationErrors(errors);

    if (text && target) {
      translation.mutate({ text, source_lang: source ?? undefined, target_lang: target });
    } else {
      reset();
    }
  };

  const onTargetLangChanged = (lang: SelectedLanguage) => {
    const targetLanguage = lang?.code ?? null;
    setTargetLang(targetLanguage);
    if (targetLanguage && srcText) {
      translate(srcText, srcLang, targetLanguage);
    } else {
      reset();
    }
  };

  const switchLanguages = () => {
    if (translation.isPending) return;

    setSrcText(translatedText);
    setSrcLang(targetLang);
    setTargetLang(srcLang);
    translate(translatedText, targetLang, srcLang);
  };

  return (
    <div className="translation-tool">
      {validationErrors.map((error, index) => (
        <div className="talaera-form-error" key={index}>
          {error.includes("Dictionary.") ? t(dynamicKey(error)) : error}
        </div>
      ))}

      <ApiErrorBanner error={translation.error} />

      <div className="translation-input">
        <LanguageSelect
          value={srcLang}
          onChange={lang => setSrcLang(lang ? lang.code : null)}
        />
        <Textarea
          value={srcText}
          placeholder={t("Dictionary.translate.sourcePlaceholder")}
          maxLength={MAX_TRANSLATION_LENGTH}
          onChange={event => setSrcText(event.target.value)}
          rtl={isRtlLang(srcLang)}
        />
      </div>

      <div className="actions">
        <Button
          size="small"
          variant="secondary"
          className="switch-btn"
          title={t("Dictionary.translate.switchLanguages")}
          aria-label={t("Dictionary.translate.switchLanguages")}
          onClick={switchLanguages}
        >
          <SwapVertIcon aria-hidden className="switch" />
        </Button>
        <Button size="small" onClick={() => translate(srcText, srcLang, targetLang)}>
          {t("Dictionary.panel.modeTranslate")}
        </Button>
      </div>

      <div className="translation-input">
        <LanguageSelect value={targetLang} onChange={onTargetLangChanged} />
        {translation.isPending ? (
          <div className="spinner-container">
            <TalaeraSpinner />
          </div>
        ) : (
          <Textarea
            value={translatedText}
            placeholder={t("Dictionary.translate.resultPlaceholder")}
            readOnly={true}
            disabled={true}
            rtl={isRtlLang(targetLang)}
          />
        )}
      </div>

      <GoogleTranslateAttributionBadge />
    </div>
  );
}
